/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React, { useCallback, useEffect, useRef } from 'react'

import { useVirtualizer } from '@tanstack/react-virtual'
import { FiChevronRight } from 'react-icons/fi'
import { TiArrowSortedDown } from 'react-icons/ti'

import { Tooltip } from '@/components/ui'

import { actionMeta, entityMeta } from './constants'
import { displayCompany, displayUser, formatCount, formatDate, formatRelative, formatTime, formatUtc } from './format'
import type { AuditRecord } from './types'

const ROW_HEIGHT = 48

interface Column {
  id: string
  label: string
  width?: number
  grow?: boolean
  minWidth?: number
  sortable?: boolean
}

interface AuditTableProps {
  rows: AuditRecord[]
  /** Server-side total, from facets. Undefined while it loads. */
  total?: number
  selectedId: string | null
  showCompany: boolean
  hasMore: boolean
  isFetchingNextPage: boolean
  loadMoreError: boolean
  /** Dims the rows during a refetch instead of flashing an empty table. */
  isRefetching: boolean
  onSelect: (record: AuditRecord) => void
  onEntityHistory: (record: AuditRecord) => void
  onLoadMore: () => void
}

export const AuditTable: React.FC<AuditTableProps> = ({
  rows,
  total,
  selectedId,
  showCompany,
  hasMore,
  isFetchingNextPage,
  loadMoreError,
  isRefetching,
  onSelect,
  onEntityHistory,
  onLoadMore,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)

  const columns: Column[] = [
    { id: 'timestamp', label: 'Timestamp', width: 150, sortable: true },
    ...(showCompany ? [{ id: 'company', label: 'Company', width: 160 } as Column] : []),
    { id: 'entityType', label: 'Entity Type', width: 220 },
    { id: 'entityName', label: 'Entity Name', grow: true, minWidth: 180 },
    { id: 'action', label: 'Action', width: 110 },
    { id: 'user', label: 'User', width: 170 },
    { id: 'summary', label: 'Summary', grow: true, minWidth: 280 },
  ]

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => containerRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 12,
  })

  // Prefetch roughly a viewport ahead so the next page is usually there before the user reaches it.
  const handleScroll = useCallback(() => {
    const el = containerRef.current
    if (!el || !hasMore || isFetchingNextPage || loadMoreError) return
    if (el.scrollHeight - el.scrollTop - el.clientHeight < el.clientHeight) {
      onLoadMore()
    }
  }, [hasMore, isFetchingNextPage, loadMoreError, onLoadMore])

  // A short first page would otherwise never trigger a scroll, stranding the list.
  useEffect(() => {
    const el = containerRef.current
    if (el && hasMore && !isFetchingNextPage && !loadMoreError && el.scrollHeight <= el.clientHeight) {
      onLoadMore()
    }
  }, [rows.length, hasMore, isFetchingNextPage, loadMoreError, onLoadMore])

  const cellStyle = (c: Column): React.CSSProperties =>
    c.grow ? { flex: 1, minWidth: c.minWidth } : { width: c.width, flex: `0 0 ${String(c.width)}px` }

  const renderCell = (c: Column, record: AuditRecord): React.ReactNode => {
    switch (c.id) {
      case 'timestamp':
        return (
          <Tooltip tip={`${formatUtc(record.loggedAt)} · ${formatRelative(record.loggedAt)}`} position="right">
            {/* The row's keyboard entry point. It is a real button rather than the row itself, so the
                entity-history button beside the name is not nested inside another interactive element. */}
            <button
              type="button"
              aria-label={`Open details: ${record.summary}`}
              onClick={(e) => {
                e.stopPropagation()
                onSelect(record)
              }}
              className="text-left leading-tight outline-none"
            >
              <div>{formatDate(record.loggedAt)}</div>
              <div className="text-xs text-base-content/55">{formatTime(record.loggedAt)}</div>
            </button>
          </Tooltip>
        )
      case 'company':
        return (
          <span className="truncate" title={displayCompany(record.companyName, record.companyId)}>
            {displayCompany(record.companyName, record.companyId)}
          </span>
        )
      case 'entityType': {
        const meta = entityMeta(record.entityType)
        return (
          <span className="flex min-w-0 items-center gap-2">
            <span className="shrink-0 text-base-content/70">{meta.icon}</span>
            <span className="truncate">{meta.label}</span>
          </span>
        )
      }
      case 'entityName':
        return (
          <span className="group/name flex min-w-0 items-center gap-1">
            <span className="truncate font-medium" title={record.entityName}>
              {record.entityName || '—'}
            </span>
            <button
              type="button"
              aria-label={`See all changes to ${record.entityName || 'this entity'}`}
              onClick={(e) => {
                e.stopPropagation()
                onEntityHistory(record)
              }}
              className="shrink-0 text-base-content/40 opacity-0 transition-opacity hover:text-base-content focus-visible:opacity-100 group-hover/name:opacity-100"
            >
              <FiChevronRight />
            </button>
          </span>
        )
      case 'action': {
        const meta = actionMeta(record.changeType)
        return (
          <span
            className={`inline-flex items-center gap-1 rounded-field px-2 py-0.5 text-[11px] font-semibold uppercase ${meta.badgeClass}`}
          >
            <span aria-hidden>{meta.icon}</span>
            {meta.label}
          </span>
        )
      }
      case 'user': {
        const user = displayUser(record.userName, record.userId)
        return (
          <span className={`truncate ${user.resolved ? '' : 'italic text-base-content/55'}`} title={user.text}>
            {user.text}
          </span>
        )
      }
      case 'summary':
        return (
          <span className="flex min-w-0 items-center gap-1.5">
            <span className="truncate text-base-content/70" title={record.summary}>
              {record.summary}
            </span>
            {record.changeCount > 1 && (
              <span className="shrink-0 rounded-selector bg-base-300 px-1.5 py-0.5 text-[10px] text-base-content/70">
                +{record.changeCount - 1} more
              </span>
            )}
          </span>
        )
      default:
        return null
    }
  }

  return (
    <div ref={containerRef} onScroll={handleScroll} className="flex-1 overflow-auto">
      {/* Header */}
      <div className="sticky top-0 z-10 flex w-full border-b border-base-300 bg-base-200 text-[11px] font-semibold uppercase tracking-wide text-base-content/60">
        {columns.map((c) => (
          <div key={c.id} style={cellStyle(c)} className="flex items-center gap-1 px-3 py-2">
            <span className="truncate">{c.label}</span>
            {/* loggedAt descending is the only ordering the API supports; offering a control the
                server cannot honour would reorder the loaded window, not the result set. */}
            {c.sortable && <TiArrowSortedDown className="text-base" aria-label="Sorted newest first" />}
          </div>
        ))}
      </div>

      {/* Virtualized rows */}
      <div
        style={{ height: virtualizer.getTotalSize(), position: 'relative' }}
        className={isRefetching ? 'opacity-50 transition-opacity' : 'transition-opacity'}
      >
        {virtualizer.getVirtualItems().map((vRow) => {
          const record = rows[vRow.index]
          const selected = record.id === selectedId

          return (
            <div
              key={record.id}
              // Mouse convenience only: keyboard and screen-reader users open the record through the
              // button in the timestamp cell.
              onClick={() => onSelect(record)}
              className={`absolute left-0 flex w-full cursor-pointer items-center border-b border-base-200 text-sm has-[button:focus-visible]:outline-2 has-[button:focus-visible]:outline-accent ${
                selected ? 'border-l-[3px] border-l-primary bg-base-300' : 'hover:bg-base-200'
              }`}
              style={{ top: 0, height: ROW_HEIGHT, transform: `translateY(${String(vRow.start)}px)` }}
            >
              {columns.map((c) => (
                <div key={c.id} style={cellStyle(c)} className="flex min-w-0 items-center px-3">
                  {renderCell(c, record)}
                </div>
              ))}
            </div>
          )
        })}
      </div>

      {/* Scroll sentinel / status strip */}
      <div aria-live="polite" className="flex h-12 items-center justify-center text-sm text-base-content/55">
        {loadMoreError ? (
          <span className="flex items-center gap-2 text-error">
            Couldn’t load more.
            <button type="button" onClick={onLoadMore} className="link link-primary">
              Retry
            </button>
          </span>
        ) : isFetchingNextPage ? (
          <span className="flex items-center gap-2">
            <span className="loading loading-spinner loading-xs" /> Loading more…
          </span>
        ) : rows.length > 0 && !hasMore ? (
          <span>— End of results{total === undefined ? '' : ` · ${formatCount(total)} changes`} —</span>
        ) : null}
      </div>
    </div>
  )
}
